// Склад: остатки со средневзвешенной себестоимостью, приход и списание
// документами, лента последних движений. Учёт включается на позицию.
import { useState } from 'react';
import { useAskSheet, useNote } from './ui/ask';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { InventoryResult, LocationsList, StockBalance, StockDocs, StockLots, type WriteOffReason } from '@dastarhan/contracts';
import { formatMoney } from '@dastarhan/core';
import { z } from 'zod';
import { downloadCsv, tg } from './csv';

const API = '/api/v1';
const UNIT_RU: Record<string, string> = { PIECE: 'шт', KG: 'кг', L: 'л', PORTION: 'порц' };
const REASONS: { v: WriteOffReason; label: string }[] = [
  { v: 'SPOILAGE', label: 'Порча' },
  { v: 'LOSS', label: 'Недостача' },
  { v: 'STAFF_MEAL', label: 'Питание персонала' },
  { v: 'OTHER', label: 'Прочее' },
];
const AdminMenuLite = z.object({
  products: z.array(z.object({
    id: z.string(), name: z.string(), unit: z.string(),
    isActive: z.boolean(), trackStock: z.boolean(), isIngredient: z.boolean(),
  })),
});
const RecipeDto = z.object({
  items: z.array(z.object({ ingredientId: z.string(), qty: z.number() })),
});

type DraftLine = { productId: string; qty: string; unitCost: string };

export function StockScreen({ token }: { token: string }) {
  const { ask, sheet } = useAskSheet();
  const { note, noteView } = useNote();
  /* Итог пересчёта — деньги: недостача и излишек остаются на экране,
     пока человек их не закроет, а не гаснут по «ОК». */
  const [invResult, setInvResult] = useState<null | {
    adjusted: number; shortageValue: number; surplusValue: number;
  }>(null);
  const qc = useQueryClient();
  const h = { Authorization: `Bearer ${token}` };
  const jh = { ...h, 'Content-Type': 'application/json' };

  const locations = useQuery({
    queryKey: ['locations'],
    queryFn: async () => LocationsList.parse(await (await fetch(`${API}/menu/locations`, { headers: h })).json()),
  });
  const [locationId, setLocationId] = useState<string | null>(null);
  const loc = locationId ?? locations.data?.[0]?.id ?? null;

  const balance = useQuery({
    queryKey: ['stock', loc],
    enabled: !!loc,
    queryFn: async () => StockBalance.parse(await (await fetch(`${API}/stock/balance?locationId=${loc}`, { headers: h })).json()),
  });
  const docs = useQuery({
    queryKey: ['stock-docs', loc],
    enabled: !!loc,
    queryFn: async () => StockDocs.parse(await (await fetch(`${API}/stock/docs?locationId=${loc}`, { headers: h })).json()),
  });
  const menu = useQuery({
    queryKey: ['admin-menu-stock'],
    queryFn: async () => AdminMenuLite.parse(await (await fetch(`${API}/menu/admin`, { headers: h })).json()),
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['stock'] });
    void qc.invalidateQueries({ queryKey: ['stock-docs'] });
    void qc.invalidateQueries({ queryKey: ['admin-menu-stock'] });
  };

  const createIngredient = useMutation({
    mutationFn: (b: { name: string; unit: string }) =>
      fetch(`${API}/stock/ingredients`, { method: 'POST', headers: jh, body: JSON.stringify(b) }),
    onSuccess: refresh,
  });
  const saveRecipe = useMutation({
    mutationFn: (b: { productId: string; items: { ingredientId: string; qty: number }[] }) =>
      fetch(`${API}/stock/recipes/${b.productId}`, {
        method: 'PUT', headers: jh, body: JSON.stringify({ items: b.items }),
      }),
    onSuccess: refresh,
  });
  const loadRecipe = async (productId: string) => {
    setRecipeDish(productId);
    setRecipeLines([]);
    if (!productId) return;
    try {
      const d = RecipeDto.parse(await (await fetch(`${API}/stock/recipes/${productId}`, { headers: h })).json());
      setRecipeLines(d.items.map((i) => ({ ingredientId: i.ingredientId, qty: String(i.qty) })));
    } catch { /* пустая тех-карта */ }
  };

  const toggleTrack = useMutation({
    mutationFn: (b: { id: string; trackStock: boolean }) =>
      fetch(`${API}/menu/products/${b.id}`, {
        method: 'PATCH', headers: jh, body: JSON.stringify({ trackStock: b.trackStock }),
      }),
    onSuccess: refresh,
  });
  const createDoc = useMutation({
    mutationFn: (b: { url: string; body: object }) =>
      fetch(`${API}${b.url}`, { method: 'POST', headers: jh, body: JSON.stringify(b.body) })
        .then(async (r) => { if (!r.ok) throw new Error((await r.json()).code ?? 'ERR'); }),
    onSuccess: refresh,
  });

  const [dialog, setDialog] = useState<null | 'supply' | 'writeoff'>(null);
  const [inv, setInv] = useState(false);
  const [invActual, setInvActual] = useState<Record<string, string>>({});
  const [invCat, setInvCat] = useState('');
  const [lotsFor, setLotsFor] = useState<{ productId: string; name: string } | null>(null);
  const lots = useQuery({
    queryKey: ['lots', lotsFor?.productId],
    enabled: !!lotsFor,
    queryFn: async () =>
      StockLots.parse(await (await fetch(`${API}/stock/lots?productId=${lotsFor!.productId}`, { headers: h })).json()),
  });
  const [ingName, setIngName] = useState('');
  const [ingUnit, setIngUnit] = useState<'KG' | 'L' | 'PIECE' | 'PORTION'>('KG');
  const [recipeDish, setRecipeDish] = useState<string>('');
  const [recipeLines, setRecipeLines] = useState<{ ingredientId: string; qty: string }[]>([]);
  const [supplier, setSupplier] = useState('');
  const [reason, setReason] = useState<WriteOffReason>('SPOILAGE');
  const [lines, setLines] = useState<DraftLine[]>([]);

  if (locations.isPending || menu.isPending) return <p className="empty">Загружаем склад…</p>;
  if (!loc) return <p className="empty">Сначала нужна точка.</p>;
  if (menu.isError || !menu.data) return <p className="empty">Не удалось загрузить. Обновите страницу.</p>;

  const tracked = menu.data.products.filter((p) => p.trackStock);
  const untracked = menu.data.products.filter((p) => p.isActive && !p.trackStock && !p.isIngredient);
  const dishes = menu.data.products.filter((p) => p.isActive && !p.isIngredient);
  const ingredients = menu.data.products.filter((p) => p.isIngredient);
  const rows = balance.data?.rows ?? [];

  const openDialog = (kind: 'supply' | 'writeoff') => {
    if (tracked.length === 0) return;
    setLines([{ productId: tracked[0]!.id, qty: '', unitCost: '' }]);
    setSupplier('');
    setReason('SPOILAGE');
    createDoc.reset();
    setDialog(kind);
  };

  const parsedLines = lines
    .map((l) => ({
      productId: l.productId,
      qty: Number(l.qty.replace(',', '.')),
      unitCost: Math.round(Number(l.unitCost.replace(',', '.')) * 100),
    }))
    .filter((l) => l.qty > 0 && (dialog === 'writeoff' || l.unitCost >= 0));
  const canSubmit = parsedLines.length > 0 && parsedLines.length === lines.filter((l) => l.qty.trim() !== '').length;

  const submit = () => {
    if (!dialog || !canSubmit) return;
    const body = dialog === 'supply'
      ? {
          locationId: loc,
          ...(supplier.trim() ? { supplier: supplier.trim() } : {}),
          items: parsedLines.map((l) => ({ productId: l.productId, qty: l.qty, unitCost: l.unitCost })),
        }
      : {
          locationId: loc,
          reason,
          items: parsedLines.map((l) => ({ productId: l.productId, qty: l.qty })),
        };
    createDoc.mutate(
      { url: dialog === 'supply' ? '/stock/supply' : '/stock/writeoff', body },
      { onSuccess: () => setDialog(null) },
    );
  };

  return (
    <div className="stock">
      <div className="menu-toolbar">
        <div className="row">
          <span className="hint-inline">Стоимость склада: <b className="money">{formatMoney(balance.data?.totalValue ?? 0)}</b></span>
          {locations.data && locations.data.length > 1 && (
            <select value={loc} onChange={(e) => setLocationId(e.target.value)}>
              {locations.data.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          )}
        </div>
        <div className="row">
          <button className="btn" disabled={rows.length === 0} onClick={() => {
            downloadCsv(`склад_${new Date().toISOString().slice(0, 10)}.csv`, [
              ['Товар', 'Ед.', 'Пришло', 'Расход', 'Списано', 'Остаток', 'Дней запаса', 'Себестоимость, ₸', 'Стоимость, ₸'],
              ...rows.map((r) => [
                r.name, UNIT_RU[r.unit] ?? r.unit, r.supplied, r.used, r.writtenOff, r.balance,
                r.daysLeft ?? '',
                r.avgCost === null ? '' : tg(r.avgCost),
                r.stockValue === null ? '' : tg(r.stockValue),
              ]),
            ]);
          }}>Скачать CSV</button>
          <button className="btn" disabled={rows.length === 0}
            onClick={() => { setInvActual({}); setInv(true); }}>Инвентаризация</button>
          <button className="btn" disabled={tracked.length === 0} onClick={() => openDialog('writeoff')}>− Списание</button>
          <button className="btn accent" disabled={tracked.length === 0} onClick={() => openDialog('supply')}>+ Приход</button>
        </div>
      </div>

      {tracked.length === 0 && (
        <p className="empty">Учёт включается на позицию — выберите ниже, что считаем. Магазин обычно включает всё, кафе — бутылки и десерты.</p>
      )}

      {rows.length > 0 && (
        <div className="rep-table stock-table">
          <div className="rep-row head">
            <span>Товар</span><span className="right">Остаток</span>
            <span className="right">Себестоимость</span><span className="right">Стоимость остатка</span><span />
          </div>
          {rows.map((r) => (
            <div key={r.productId} className="rep-row">
              <span>{r.name} <small className="hint-inline">пришло {r.supplied} · расход {r.used} · списано {r.writtenOff}</small></span>
              <span className={`right ${r.balance < 0 ? 'neg' : ''}`}>
                {r.balance} {UNIT_RU[r.unit] ?? ''}
                {r.daysLeft != null && (
                  <small className="hint-inline" style={r.daysLeft <= 3 ? { color: 'var(--danger, #c0392b)' } : undefined}>
                    {' '}· ≈{r.daysLeft} дн.
                  </small>
                )}
                {r.balance < 0 && <small> · продавали неоприходованное</small>}
              </span>
              <span className="money right">{r.avgCost === null ? '—' : `${formatMoney(r.avgCost)}`}</span>
              <span className="money right">{r.stockValue === null ? '—' : formatMoney(r.stockValue)}</span>
              <span className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                <button className="btn small" title="История закупочных цен"
                  onClick={() => setLotsFor({ productId: r.productId, name: r.name })}>Цены</button>
                <button className="btn small" onClick={() => toggleTrack.mutate({ id: r.productId, trackStock: false })}>
                  Не учитывать
                </button>
              </span>
            </div>
          ))}
        </div>
      )}

      {untracked.length > 0 && (
        <section className="menu-cat" style={{ marginTop: 16 }}>
          <header><h2>Что ещё можно учитывать</h2></header>
          <div className="link-row" style={{ borderTop: 0 }}>
            {untracked.map((p) => (
              <button key={p.id} className="chip chip-add"
                onClick={() => toggleTrack.mutate({ id: p.id, trackStock: true })}>
                + {p.name}
              </button>
            ))}
          </div>
        </section>
      )}

      <section className="menu-cat" style={{ marginTop: 16 }}>
        <header><h2>Сырьё</h2><span className="rule-tag">{ingredients.length}</span></header>
        <div className="promo-form">
          <p className="hint-inline" style={{ margin: 0 }}>
            Сырьё живёт на складе и в тех-картах, на кассе не продаётся. Мука, мясо, сироп — сюда.
          </p>
          <div className="row">
            <input placeholder="Название: «Мука в/с»…" value={ingName}
              onChange={(e) => setIngName(e.target.value)} />
            <select value={ingUnit} onChange={(e) => setIngUnit(e.target.value as typeof ingUnit)}>
              <option value="KG">кг</option><option value="L">л</option>
              <option value="PIECE">шт</option><option value="PORTION">порц</option>
            </select>
            <button className="btn small" disabled={!ingName.trim() || createIngredient.isPending}
              onClick={() => { createIngredient.mutate({ name: ingName.trim(), unit: ingUnit }); setIngName(''); }}>
              Добавить
            </button>
          </div>
          {ingredients.length > 0 && (
            <div className="link-row" style={{ borderTop: 0, padding: 0 }}>
              {ingredients.map((p) => (
                <span key={p.id} className="chip" style={{ cursor: 'default' }}>
                  {p.name} · {UNIT_RU[p.unit] ?? p.unit}
                </span>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="menu-cat" style={{ marginTop: 16 }}>
        <header>
          <h2>Тех-карты</h2>
          <button className="btn small" onClick={() => {
            void (async () => {
              const r = await fetch(`${API}/stock/recipes-export`, { headers: h });
              if (!r.ok) { note('Не получилось выгрузить тех-карты', 'bad'); return; }
              const d = (await r.json()) as { rows: { dish: string; ingredient: string; qty: number; unit: string }[] };
              if (d.rows.length === 0) { note('Тех-карт пока нет — выгружать нечего', 'bad'); return; }
              downloadCsv('тех-карты.csv', [
                ['Блюдо', 'Ингредиент', 'На порцию', 'Ед'],
                ...d.rows.map((x) => [x.dish, x.ingredient, x.qty, UNIT_RU[x.unit] ?? x.unit]),
              ]);
            })();
          }}>Скачать все (CSV)</button>
        </header>
        <div className="promo-form">
          <p className="hint-inline" style={{ margin: 0 }}>
            Сколько сырья уходит на одну порцию. Продажа блюда списывает ингредиенты по этим строкам.
          </p>
          <div className="row">
            <select value={recipeDish} onChange={(e) => void loadRecipe(e.target.value)}>
              <option value="">— выберите блюдо —</option>
              {dishes.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          {recipeDish && tracked.length === 0 && (
            <p className="hint-inline" style={{ margin: 0 }}>Сначала добавьте сырьё выше — списывать пока нечего.</p>
          )}
          {recipeDish && tracked.length > 0 && (
            <>
              {recipeLines.map((l, i) => (
                <div key={i} className="row stock-line" style={{ gridTemplateColumns: '1fr 120px 40px' }}>
                  <select value={l.ingredientId}
                    onChange={(e) => setRecipeLines(recipeLines.map((x, j) => j === i ? { ...x, ingredientId: e.target.value } : x))}>
                    {tracked.map((p) => (
                      <option key={p.id} value={p.id}>{p.name} ({UNIT_RU[p.unit] ?? p.unit})</option>
                    ))}
                  </select>
                  <input placeholder="На порцию" inputMode="decimal" value={l.qty}
                    onChange={(e) => setRecipeLines(recipeLines.map((x, j) => j === i ? { ...x, qty: e.target.value } : x))} />
                  <button className="btn small" type="button" aria-label="Убрать строку"
                    onClick={() => setRecipeLines(recipeLines.filter((_, j) => j !== i))}>✕</button>
                </div>
              ))}
              <div className="row">
                <button className="btn small" type="button"
                  onClick={() => setRecipeLines([...recipeLines, { ingredientId: tracked[0]!.id, qty: '' }])}>
                  + Ингредиент
                </button>
                <button className="btn accent small" type="button" disabled={saveRecipe.isPending}
                  onClick={() => {
                    const items = recipeLines
                      .map((l) => ({ ingredientId: l.ingredientId, qty: Number(l.qty.replace(',', '.')) }))
                      .filter((l) => l.qty > 0);
                    saveRecipe.mutate({ productId: recipeDish, items });
                  }}>
                  Сохранить тех-карту
                </button>
              </div>
            </>
          )}
        </div>
      </section>

      {(docs.data?.rows.length ?? 0) > 0 && (
        <section className="menu-cat" style={{ marginTop: 16 }}>
          <header><h2>Последние движения</h2></header>
          {docs.data!.rows.map((d) => (
            <div key={d.id} className="menu-row doc-row">
              <span className={`doc-kind ${d.kind === 'SUPPLY' ? 'in' : 'out'}`}>
                {d.kind === 'SUPPLY' ? '↓' : d.kind === 'INVENTORY' ? '≡' : '↑'}
              </span>
              <span className="name">{d.title} <small className="hint-inline">
                {new Date(d.at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                {' '}· {d.itemsCount} поз.</small></span>
              <span className="money right">{d.total === null ? '' : formatMoney(d.total)}</span>
            </div>
          ))}
        </section>
      )}

      {inv && (
        <div className="dialog-backdrop" role="dialog" aria-modal="true" onClick={() => setInv(false)}>
          <div className="office-dialog stock-dialog" onClick={(e) => e.stopPropagation()}>
            <h2>Инвентаризация</h2>
            <label className="row" style={{ gap: 8, alignItems: 'center' }}>
              <span className="hint-inline">Считаем:</span>
              <select value={invCat} onChange={(e) => setInvCat(e.target.value)}>
                <option value="">всё подряд</option>
                {[...new Set(rows.map((r) => r.categoryName ?? 'Прочее'))].sort().map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </label>
            <p className="hint-inline" style={{ margin: 0 }}>
              Впишите фактические остатки. Пустое поле — позицию не считали.
              Расхождения проведутся приходом/списанием, акт сохранится.
            </p>
            <div className="inv-list">
              {rows.filter((r) => !invCat || (r.categoryName ?? 'Прочее') === invCat).map((r) => (
                <div key={r.productId} className="row inv-line">
                  <span className="name">{r.name}</span>
                  <span className="hint-inline right">расчёт: {r.balance} {UNIT_RU[r.unit] ?? ''}</span>
                  <input inputMode="decimal" placeholder="факт"
                    value={invActual[r.productId] ?? ''}
                    onChange={(e) => setInvActual({ ...invActual, [r.productId]: e.target.value })} />
                </div>
              ))}
            </div>
            <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn" onClick={() => setInv(false)}>Отмена</button>
              <button className="btn accent" onClick={() => void (async () => {
                const items = Object.entries(invActual)
                  .map(([productId, v]) => ({ productId, actual: Number(v.replace(',', '.')) }))
                  .filter((i) => Number.isFinite(i.actual) && i.actual >= 0);
                if (items.length === 0) { note('Ни одна позиция не посчитана', 'bad'); return; }
                const a = await ask({
                  title: 'Провести пересчёт',
                  sub: 'Остатки станут равны тому, что вы насчитали. Разница спишется как недостача или зайдёт излишком — отменить это нельзя.',
                  effects: [
                    ['Посчитано позиций', String(items.length)],
                    ['Что изменится', 'остатки склада на этой точке'],
                    ['Отменить', 'одним действием нельзя'],
                  ],
                  danger: true,
                  confirmLabel: 'Провести пересчёт',
                });
                if (!a) return;
                const r = await fetch(`${API}/stock/inventory`, {
                  method: 'POST', headers: jh, body: JSON.stringify({ locationId: loc, items }),
                });
                if (!r.ok) { note('Не получилось провести пересчёт', 'bad'); return; }
                const d = InventoryResult.parse(await r.json());
                setInvResult(d);
                setInv(false);
                refresh();
              })()}>Провести</button>
            </div>
          </div>
        </div>
      )}

      {lotsFor && (
        <div className="dialog-backdrop" role="dialog" aria-modal="true" onClick={() => setLotsFor(null)}>
          <div className="office-dialog" onClick={(e) => e.stopPropagation()}>
            <h2>Закупки: {lotsFor.name}</h2>
            {lots.isPending && <p className="dlg-line">Загружаем…</p>}
            {lots.data && lots.data.rows.length === 0 && (
              <p className="dlg-line">Приходов ещё не было.</p>
            )}
            {lots.data && lots.data.rows.length > 0 && (
              <>
                {lots.data.avgCost !== null && (
                  <p className="dlg-line">Средневзвешенная: <b className="money">{formatMoney(lots.data.avgCost)}</b>/ед</p>
                )}
                <div className="lots-list">
                  {lots.data.rows.map((l, i) => {
                    const diff = lots.data!.avgCost === null || lots.data!.avgCost === 0
                      ? null
                      : (l.unitCost - lots.data!.avgCost) / lots.data!.avgCost;
                    return (
                      <div key={i} className="lots-row">
                        <span>{new Date(l.at).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' })}</span>
                        <span className="hint-inline">{l.supplier ?? '—'}</span>
                        <span className="right">{l.qty}</span>
                        <span className="money right">{formatMoney(l.unitCost)}</span>
                        <span className={`right ${diff !== null && diff > 0.05 ? 'neg' : 'hint-inline'}`}>
                          {diff === null ? '' : `${diff > 0 ? '+' : ''}${(diff * 100).toFixed(0)}%`}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <button className="btn" onClick={() => setLotsFor(null)}>Закрыть</button>
            </div>
          </div>
        </div>
      )}

      {dialog && (
        <div className="dialog-backdrop" role="dialog" aria-modal="true" onClick={() => setDialog(null)}>
          <div className="office-dialog stock-dialog" onClick={(e) => e.stopPropagation()}>
            <h2>{dialog === 'supply' ? 'Приход товара' : 'Списание'}</h2>
            {dialog === 'supply' ? (
              <label>Поставщик (необязательно)
                <input value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="ИП Досжанов" />
              </label>
            ) : (
              <label>Причина
                <select value={reason} onChange={(e) => setReason(e.target.value as WriteOffReason)}>
                  {REASONS.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
                </select>
              </label>
            )}

            {lines.map((l, i) => (
              <div key={i} className="row stock-line">
                <select value={l.productId}
                  onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, productId: e.target.value } : x))}>
                  {tracked.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <input placeholder="Кол-во" inputMode="decimal" value={l.qty}
                  onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, qty: e.target.value } : x))} />
                {dialog === 'supply' && (
                  <input placeholder="Цена, ₸/ед" inputMode="decimal" value={l.unitCost}
                    onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, unitCost: e.target.value } : x))} />
                )}
                <button className="btn small" type="button" aria-label="Убрать строку"
                  onClick={() => setLines(lines.filter((_, j) => j !== i))}>✕</button>
              </div>
            ))}
            <button className="btn small" style={{ alignSelf: 'flex-start' }} type="button"
              onClick={() => setLines([...lines, { productId: tracked[0]!.id, qty: '', unitCost: '' }])}>
              + Строка
            </button>

            {createDoc.isError && <p className="dlg-err">Не получилось сохранить документ.</p>}
            <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn" type="button" onClick={() => setDialog(null)}>Отмена</button>
              <button className="btn accent" type="button" disabled={!canSubmit || createDoc.isPending} onClick={submit}>
                Провести
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Итог пересчёта: недостача и излишек остаются на экране,
          пока человек их не закроет — по ним объясняются с кухней. */}
      {invResult && (
        <div className="ask-back" onMouseDown={(e) => e.target === e.currentTarget && setInvResult(null)}>
          <div className="ask-card" role="dialog" aria-modal="true" aria-label="Итог пересчёта">
            <h2>{invResult.adjusted === 0 ? 'Всё сошлось' : 'Пересчёт проведён'}</h2>
            {invResult.adjusted === 0 ? (
              <p className="ask-sub">Расхождений нет — остатки совпали с тем, что насчитали.</p>
            ) : (
              <>
                <p className="ask-sub">
                  Остатки обновлены. Расхождения ниже — по ним объясняются с кухней и баром.
                </p>
                <dl className="ask-effects">
                  <div><dt>Позиций разошлось</dt><dd>{invResult.adjusted}</dd></div>
                  <div>
                    <dt>Недостача</dt>
                    <dd className="inv-short">{(invResult.shortageValue / 100).toLocaleString('ru-RU')} ₸</dd>
                  </div>
                  <div>
                    <dt>Излишек</dt>
                    <dd>{(invResult.surplusValue / 100).toLocaleString('ru-RU')} ₸</dd>
                  </div>
                </dl>
              </>
            )}
            <div className="ask-actions">
              <button className="btn primary" onClick={() => setInvResult(null)}>Понятно</button>
            </div>
          </div>
        </div>
      )}
      {sheet}
      {noteView}
    </div>
  );
}
